import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SettingsPage } from '../components/SettingsPage';
import { BookingAside } from '../components/SettingsAsides';
import { useParams } from '@tanstack/react-router';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { PageHeader, SavedNote, Section, Select, Switch } from '../components/forms';
import { Button, ErrorNote } from '../components/ui';
import { api, ApiError, type Salon } from '../lib/api';
import { useT } from '../lib/i18n';
import { useLocations } from '../lib/location';

type Rules = { leadMinutes: number; maxDaysAhead: number; stepMinutes: number; allowStaffChoice: boolean; changeUntilHours: number; emailRequired: boolean; waitlist?: boolean };
type Settings = { slug: string; bookingEnabled: boolean; rules: Rules };

/** Where the booking pages live: the app's own domain in production, the Astro dev server locally. */
const bookingOrigin = () => (import.meta.env.VITE_BOOKING_ORIGIN as string | undefined) ?? window.location.origin;
export const bookingUrl = (slug: string) => `${bookingOrigin()}/boek/${slug}`;

function markShared(orgId: string) {
  try {
    localStorage.setItem(`shared-link:${orgId}`, '1');
  } catch {
    // storage unavailable
  }
}

/** "Deel je boekingslink": the phone share sheet when there is one, otherwise copy. */
export function ShareLinkButton({ orgId, slug, variant = 'secondary' }: { orgId: string; slug: string; variant?: 'primary' | 'secondary' | 'dark' | 'cream' }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  async function share() {
    const url = bookingUrl(slug);
    markShared(orgId);
    if (navigator.share && window.matchMedia('(pointer: coarse)').matches) {
      try {
        await navigator.share({ url, title: t('booking.shareTitle') });
        return;
      } catch {
        // cancelled: fall back to copying
      }
    }
    await navigator.clipboard.writeText(url);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2500);
  }
  return (
    <Button variant={variant} onClick={share}>
      {copied ? `✓ ${t('booking.copied')}` : t('booking.share')}
    </Button>
  );
}

export function BookingSettingsPage() {
  const { orgId } = useParams({ from: '/s/$orgId' });
  const t = useT();
  const qc = useQueryClient();
  const key = ['booking-settings', orgId];
  const settings = useQuery({ queryKey: key, queryFn: () => api<Settings>(`/orgs/${orgId}/booking-settings`) });
  const salon = useQuery({ queryKey: ['salon', orgId], queryFn: () => api<Salon>(`/orgs/${orgId}`) });
  const locations = useLocations(orgId);
  const [draft, setDraft] = useState<Settings | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (settings.data && !draft) setDraft(settings.data);
  }, [settings.data, draft]);
  const save = useMutation({
    mutationFn: (s: Settings) => api<Settings>(`/orgs/${orgId}/booking-settings`, { method: 'PUT', body: JSON.stringify({ bookingEnabled: s.bookingEnabled, rules: s.rules }) }),
    onSuccess: (d) => {
      qc.setQueryData(key, d);
      setDraft(d);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    },
  });

  const [qr, setQr] = useState<string | null>(null);
  const slug = settings.data?.slug;
  useEffect(() => {
    if (!slug) return;
    void QRCode.toDataURL(bookingUrl(slug), { margin: 1, width: 360, color: { dark: '#1b201e', light: '#ffffff' } }).then(setQr);
  }, [slug]);

  const manager = salon.data?.role !== 'staff';
  if (!draft || !slug) return <p className="text-muted">{t('common.loading')}</p>;
  const url = bookingUrl(slug);
  const button = `<script src="${bookingOrigin()}/boek/widget.js" data-salon="${slug}" async></script>`;
  const inline = `<script src="${bookingOrigin()}/boek/widget.js" data-salon="${slug}" data-inline async></script>`;
  const set = (patch: Partial<Rules>) => setDraft({ ...draft, rules: { ...draft.rules, ...patch } });
  const changed = JSON.stringify(draft) !== JSON.stringify(settings.data);

  return (
    <SettingsPage orgId={orgId} aside={<BookingAside orgId={orgId} enabled={draft.bookingEnabled} staffChoice={draft.rules.allowStaffChoice} />}>
      <PageHeader orgId={orgId} title={t('booking.title')} intro={t('booking.intro')} />

      <Section title={t('booking.link')} intro={t('booking.linkIntro')}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          <div className="flex min-w-0 flex-1 flex-col gap-3">
            <p className="break-all rounded-[var(--radius-control)] bg-subtle px-3 py-2 font-medium">{url}</p>
            <div className="flex flex-wrap gap-2">
              <ShareLinkButton orgId={orgId} slug={slug} />
              <a href={url} target="_blank" rel="noreferrer" className="inline-flex min-h-11 items-center rounded-full px-4 text-[15px] font-semibold text-accent-strong hover:bg-subtle" onClick={() => markShared(orgId)}>
                {t('booking.open')} ↗
              </a>
            </div>
            <p className="text-sm text-muted">{t('booking.linkWhere')}</p>
            {(locations.data?.length ?? 0) > 1 && (
              <div className="flex flex-col gap-1">
                <p className="text-sm font-semibold">{t('loc.bookingLinks')}</p>
                {locations.data!.map((l) => (
                  <p key={l.id} className="break-all text-sm">
                    <span className="font-medium">{l.name}:</span> <span className="text-muted">{`${url}?locatie=${l.slug}`}</span>
                  </p>
                ))}
              </div>
            )}
          </div>
          {qr && (
            <div className="flex flex-col items-center gap-2">
              <img src={qr} alt={t('booking.qrAlt')} className="size-36 rounded-[var(--radius-control)] border border-line" />
              <a href={qr} download={`boekingslink-${slug}.png`} className="text-sm font-semibold text-accent-strong hover:underline">
                {t('booking.qrDownload')}
              </a>
            </div>
          )}
        </div>
      </Section>

      <Section title={t('booking.website')} intro={t('booking.websiteIntro')}>
        <CodeBlock label={t('booking.websiteButton')} code={button} />
        <CodeBlock label={t('booking.websiteInline')} code={inline} />
      </Section>

      <Section title={t('booking.rules')}>
        <fieldset disabled={!manager} className="flex flex-col gap-4">
          <Switch label={t('booking.enabled')} hint={t('booking.enabledHint')} checked={draft.bookingEnabled} onChange={(v) => setDraft({ ...draft, bookingEnabled: v })} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Select id="lead" label={t('booking.lead')} value={draft.rules.leadMinutes} onChange={(e) => set({ leadMinutes: Number(e.target.value) })}>
              {[0, 30, 60, 120, 240, 720, 1440, 2880].map((m) => (
                <option key={m} value={m}>
                  {m === 0 ? t('booking.leadNone') : m < 60 ? `${m} ${t('common.minutes')}` : m < 1440 ? t('booking.hours', { n: m / 60 }) : t('booking.days', { n: m / 1440 })}
                </option>
              ))}
            </Select>
            <Select id="ahead" label={t('booking.ahead')} value={draft.rules.maxDaysAhead} onChange={(e) => set({ maxDaysAhead: Number(e.target.value) })}>
              {[
                [14, t('booking.weeks', { n: 2 })],
                [31, t('booking.months', { n: 1 })],
                [62, t('booking.months', { n: 2 })],
                [92, t('booking.months', { n: 3 })],
                [183, t('booking.months', { n: 6 })],
                [365, t('booking.year')],
              ].map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </Select>
            <Select id="step" label={t('booking.step')} hint={t('booking.stepHint')} value={draft.rules.stepMinutes} onChange={(e) => set({ stepMinutes: Number(e.target.value) })}>
              {[10, 15, 20, 30, 60].map((m) => (
                <option key={m} value={m}>
                  {t('booking.every', { n: m })}
                </option>
              ))}
            </Select>
            <Select id="change" label={t('booking.change')} hint={t('booking.changeHint')} value={draft.rules.changeUntilHours} onChange={(e) => set({ changeUntilHours: Number(e.target.value) })}>
              {[0, 2, 12, 24, 48].map((h) => (
                <option key={h} value={h}>
                  {h === 0 ? t('booking.changeAlways') : t('booking.hoursBefore', { n: h })}
                </option>
              ))}
            </Select>
          </div>
          <Switch label={t('booking.staffChoice')} hint={t('booking.staffChoiceHint')} checked={draft.rules.allowStaffChoice} onChange={(v) => set({ allowStaffChoice: v })} />
          <Switch label={t('booking.waitlist')} hint={t('booking.waitlistHint')} checked={!!draft.rules.waitlist} onChange={(v) => set({ waitlist: v })} />
        </fieldset>
        <ErrorNote message={save.error instanceof ApiError ? save.error.message : save.error ? t('error.generic') : null} />
        {manager && (
          <div className="flex items-center gap-3">
            <Button onClick={() => save.mutate(draft)} disabled={!changed || save.isPending}>
              {t('common.save')}
            </Button>
            <SavedNote show={saved} />
          </div>
        )}
      </Section>
    </SettingsPage>
  );
}

function CodeBlock({ label, code }: { label: string; code: string }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold">{label}</span>
        <button
          type="button"
          className="text-sm font-semibold text-accent-strong hover:underline"
          onClick={async () => {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
          }}
        >
          {copied ? `✓ ${t('booking.copied')}` : t('booking.copy')}
        </button>
      </div>
      <code className="block overflow-x-auto whitespace-pre rounded-[var(--radius-control)] bg-ink px-3 py-2 text-[13px] text-surface">{code}</code>
    </div>
  );
}
